import { DeviceMediaRequest, MediaRequestStatuses, getMediaRequestStatusesDisplayName } from "@app/shared/src/models";

/**
 * One reading of a media request's lifecycle for every console screen that shows one.
 *
 * Mirrors the dashboard's status column so a director looking at both sees the same word for
 * the same clip: the unit reports Scheduled → Recording → Merging → Uploading → Complete, and
 * the dashboard folds the three merge stages into "Processing" and calls a Scheduled request
 * whose window has already passed "Requested". The console used to collapse everything before
 * the upload into "Queued", which read as "nothing has happened yet" while the unit was in fact
 * recording or encoding.
 */

/** Statuses that mean the unit is still working on it — the only ones worth polling through. */
export const MEDIA_REQUEST_WORKING = new Set<MediaRequestStatuses>([
    MediaRequestStatuses.Scheduled,
    MediaRequestStatuses.Recording,
    MediaRequestStatuses.Merging,
    MediaRequestStatuses.MergingVideo,
    MediaRequestStatuses.MergingRadar,
    MediaRequestStatuses.Uploading,
]);

export type MediaRequestPhase = "ready" | "working" | "failed";

export type MediaRequestTone = "safe" | "warn" | "crit" | "mute";

export type MediaRequestStatusView = { label: string; tone: MediaRequestTone };

/** The fields the reading depends on — a plain object is fine, the class is not required. */
export type MediaRequestStatusInput = Pick<DeviceMediaRequest, "status" | "progress" | "bytesPerSecond"> & {
    videoUrl?: string | null;
    periodEnd?: Date | string | null;
};

export function isMediaRequestWorking(r: Pick<DeviceMediaRequest, "status"> | undefined): boolean {
    return r?.status !== undefined && MEDIA_REQUEST_WORKING.has(r.status);
}

/**
 * Which filter bucket a request lands in. "ready" is the presence of video — a PartiallyComplete
 * clip carries video too — and "failed" is anything terminal that produced none, so that an
 * expired or cancelled pull never sits under "working" forever.
 */
export function mediaRequestPhase(r: MediaRequestStatusInput): MediaRequestPhase {
    if (r.videoUrl) return "ready";
    if (isMediaRequestWorking(r)) return "working";
    return "failed";
}

/** Bytes per second the way an upload speed reads: KB/s below a megabyte, MB/s above. */
export function formatTransferRate(bytesPerSecond: number): string {
    if (bytesPerSecond >= 1_000_000) return `${(bytesPerSecond / 1_000_000).toFixed(1)} MB/s`;
    return `${Math.round(bytesPerSecond / 1000)} KB/s`;
}

export function describeMediaRequestStatus(r: MediaRequestStatusInput, now: Date = new Date()): MediaRequestStatusView {
    switch (r.status) {
        case MediaRequestStatuses.Uploading: {
            const pct = Math.round((r.progress ?? 0) * 100);
            const rate = r.bytesPerSecond ? ` at ${formatTransferRate(r.bytesPerSecond)}` : "";
            return { label: `Uploading ${pct}%${rate}`, tone: "warn" };
        }
        case MediaRequestStatuses.Scheduled: {
            // A window still in the future is genuinely scheduled; one already past is just
            // waiting for the unit to pick it up on its next check-in.
            const end = r.periodEnd ? new Date(r.periodEnd) : undefined;
            const future = end !== undefined && !Number.isNaN(end.getTime()) && end > now;
            return { label: future ? "Scheduled" : "Requested", tone: "mute" };
        }
        case MediaRequestStatuses.Recording:
            return { label: "Recording", tone: "warn" };
        case MediaRequestStatuses.Merging:
        case MediaRequestStatuses.MergingVideo:
        case MediaRequestStatuses.MergingRadar:
            return { label: "Processing", tone: "warn" };
        case MediaRequestStatuses.PartiallyComplete:
            return { label: "Partially complete", tone: "warn" };
        case MediaRequestStatuses.Complete:
            // "Ready" is the console's word for "there is a clip to watch"; a completed request
            // with no video (radar-only) is done but not watchable.
            return { label: r.videoUrl ? "Ready" : "Complete", tone: "safe" };
        case MediaRequestStatuses.Expired:
            return { label: "Expired", tone: "mute" };
        case MediaRequestStatuses.Cancelled:
            return { label: "Cancelled", tone: "mute" };
        case MediaRequestStatuses.Error:
            return { label: "Failed", tone: "crit" };
        default:
            return { label: getMediaRequestStatusesDisplayName(r.status) ?? "Requested", tone: "mute" };
    }
}
